import React, { useEffect, useRef, useState } from 'react';
import { savePath } from '../api/gameBuilderAPI';

/**
 * The creator reviews a mission before anything is built (backend mission_path.py): the objects in the room and how each
 * must END, in plain words ("Condenser hose: connected", "Ether bottle in Flammables cabinet"). Change a line, delete it,
 * add one, add an object. Every choice comes from what that object can do (the server's `menu`). Each edit is saved; the
 * server repairs what the game couldn't play and says so - it never blocks. The game's mechanics (what blocks what, how
 * it ends, the moves) are worked out by code. "Approve design" writes the words (AI call 2) and builds it.
 */
const SAVE_DELAY_MS = 400;

const has = (v) => v !== undefined && v !== null && v !== '';
const single = (line) => (Array.isArray(line.object) ? line.object.map((o) => ({ ...line, object: o })) : [line]);
// (a "says…" line is one before anything is typed in it - `has: ''`)
const valueOf = (line) => (line.status ? `status:${line.status}` : line.relation ? `rel:${line.relation}:${line.target}`
  : line.has !== undefined && line.has !== null ? 'has' : '');
const lineFor = (object, value, old = {}) => {
  const [kind, a, b] = value.split(':');
  if (kind === 'status') return { object, status: a };
  if (kind === 'rel') return { object, relation: a, target: b };
  return { object, has: has(old.has) ? old.has : '', ...(old.unit ? { unit: old.unit } : {}) };
};
/** A line with only the fields the server's schema takes. */
const clean = (line) => Object.fromEntries(Object.entries(line).filter(([k, v]) => has(v) && (k !== 'last' || v)));
// a line still being written - "says…" with nothing typed yet: kept on screen, not saved until it says something
const blank = (line) => !Array.isArray(line) && (valueOf(line) === '' || (valueOf(line) === 'has' && !has(line.has)));
const slug = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'object';
const isPlace = (line) => line.relation && line.relation !== 'behind';
// one object per line, so each row reads "object: how it ends"
const flat = (path) => ({ ...path, end: path.end.flatMap((x) => (Array.isArray(x) ? [x] : single(x))) });
// what a new object IS (backend traits.py): picked up, pushed, or fixed; what goes in / on it
const CARRY = [['carry', 'Carried'], ['push', 'Pushed (too big to carry)'], ['no', 'Fixed in place']];
const HOLDS = [['', 'Holds nothing'], ['in', 'Things go in it'], ['on', 'Things go on it']];
const NEW = 'new';
const noTraits = { carry: undefined, holds: undefined, inside: undefined, noun: undefined };

export default function PathEditor({ planId, mission, view, onChange, onApprove, busy }) {
  const [path, setPath] = useState(() => flat(view.path));
  const [menu, setMenu] = useState(view.menu || { objects: {}, types: [] });
  const [notes, setNotes] = useState(view.notes || []);
  // what the creator sees first: the job in plain words (the server's `plain`); the editor only when they ask for it
  const [plain, setPlain] = useState(view.plain || null);
  const [editing, setEditing] = useState(!view.plain);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);   // edited since it was approved: approving again rebuilds the mission
  const [error, setError] = useState('');
  const timer = useRef(null);
  const latest = useRef(path);                     // what's on screen - the server's copy doesn't replace a line being written
  const pending = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = async () => {
    clearTimeout(timer.current);
    const next = pending.current;
    if (!next) return true;
    pending.current = null;
    setSaving(true);
    try {
      const saved = await savePath(planId, mission.id, next);
      // the server's version: what the game can't play is gone (said in a note) - unless the creator has typed on since
      if (!pending.current && saved.path && !latest.current.end.some(blank)) setPath(flat(saved.path));
      setMenu(saved.menu); setNotes(saved.notes || []); setPlain(saved.plain || null); setError('');
      onChange?.(saved);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };
  const update = (next) => {
    setPath(next);
    latest.current = next;
    setChanged(true);
    pending.current = { ...next, start: next.start.map(clean),
      end: next.end.filter((x) => !blank(x)).map((x) => (Array.isArray(x) ? x.map(clean) : clean(x))) };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY_MS);
  };

  const m = (id) => menu.objects[id];
  const optionsFor = (id, list, current) => {
    const opts = (m(id) || {})[list] || [];
    return current && !opts.some((o) => o.value === current) ? [...opts, { value: current, label: current.split(':').pop() }] : opts;
  };

  // ---- the room: each object, how it starts (a state, a place), what it says
  const startLine = (id, kind) => path.start.find((x) => x.object === id && (kind === 'status' ? x.status : kind === 'place' ? isPlace(x) : x.event === 'talks'));
  const setStart = (id, kind, line) => {
    const others = path.start.filter((x) => !(x.object === id && (kind === 'status' ? x.status : kind === 'place' ? isPlace(x) : x.event === 'talks')));
    update({ ...path, start: line ? [...others, line] : others });
  };
  const setObject = (n, patch) => update({ ...path, objects: path.objects.map((o, k) => (k === n ? { ...o, ...patch } : o)) });
  const addObject = () => {
    let id = 'new_object';
    for (let n = 2; path.objects.some((o) => o.id === id); n += 1) id = `new_object_${n}`;
    update({ ...path, objects: [...path.objects, { id, type: 'thing', name: 'New object', carry: 'carry' }] });
  };
  // a ready-made item, or "new" - then what it is: carried / pushed / fixed, what it holds, its two states
  const setKind = (n, value) => (value === NEW
    ? setObject(n, { type: 'thing', carry: 'carry', holds: undefined, states: undefined, inside: undefined })
    : setObject(n, { ...noTraits, type: value, states: undefined, do: undefined }));
  const setWord = (n, k, word) => {
    const o = path.objects[n];
    const words = [...(o.states || ['', ''])];
    words[k] = word;
    setObject(n, { states: words.some((w) => w.trim()) ? words : undefined });
  };
  const renameObject = (n, name) => {
    const o = path.objects[n];
    // a new object takes its id from its name, so lines can name it
    if (o.id.startsWith('new_object') && !path.end.flat().some((x) => x.object === o.id || x.target === o.id)) {
      let id = slug(name);
      for (let k = 2; path.objects.some((x, j) => j !== n && x.id === id); k += 1) id = `${slug(name)}_${k}`;
      setObject(n, { name, id });
    } else setObject(n, { name });
  };
  const removeObject = (n) => {
    const gone = path.objects[n].id;
    const uses = (x) => x.object === gone || (Array.isArray(x.object) && x.object.includes(gone)) || x.target === gone;
    update({ ...path, objects: path.objects.filter((_, k) => k !== n), start: path.start.filter((x) => !uses(x)),
      end: path.end.map((x) => (Array.isArray(x) ? x.filter((y) => !uses(y)) : x)).filter((x) => (Array.isArray(x) ? x.length : !uses(x))) });
  };

  // ---- how it must end
  const setEnd = (i, line) => update({ ...path, end: path.end.map((x, k) => (k === i ? line : x)) });
  const removeEnd = (i) => update({ ...path, end: path.end.filter((_, k) => k !== i) });
  const canEnd = (id) => ((m(id) || {}).end || []).length > 0;
  // A new line is one that isn't there yet (a repeat is dropped when saved): first a thing with no line, then another
  // kind of ending (a state, a place, a value) for a thing that has one - never a second state for the same thing.
  const addEnd = () => {
    const kindOf = (value) => value.split(':')[0];
    const lines = path.end.flat();
    const usedKinds = (id) => lines.filter((x) => x.object === id).map((x) => kindOf(valueOf(x)));
    const ending = (id) => optionsFor(id, 'end').find((x) => !usedKinds(id).includes(kindOf(x.value)));
    const able = path.objects.filter((o) => canEnd(o.id));
    const pick = able.find((o) => !usedKinds(o.id).length) || able.find((o) => ending(o.id));
    if (!able.length) {
      setError('Nothing in this room can change yet - give an object two states, or add something that holds things.');
      return;
    }
    if (!pick) {
      setError('Every object already has each kind of ending - change a line, or add an object first.');
      return;
    }
    setError('');
    update({ ...path, end: [...path.end, lineFor(pick.id, ending(pick.id).value)] });
  };

  const endRow = (line, i) => {
    const value = valueOf(line);
    const mo = m(line.object) || {};
    return (
      <li key={`end-${i}`} className="gb-path-row">
        <div className="gb-path-line">
          <select aria-label="Object" value={line.object} onChange={(e) => {
            setEnd(i, lineFor(e.target.value, optionsFor(e.target.value, 'end')[0].value, line));
          }}>
            {path.objects.filter((o) => canEnd(o.id) || o.id === line.object).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            {!path.objects.some((o) => o.id === line.object) && <option value={line.object}>{line.object}</option>}
          </select>
          <select aria-label="Ends" value={value} onChange={(e) => setEnd(i, lineFor(line.object, e.target.value, line))}>
            {optionsFor(line.object, 'end', value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {value === 'has' && (
            <>
              <input type={mo.unit ? 'number' : 'text'} step="any" aria-label="Value" value={line.has ?? ''} autoFocus={!has(line.has)}
                placeholder={mo.unit ? 'amount' : 'what it must say'} className={has(line.has) ? '' : 'is-blank'}
                onChange={(e) => setEnd(i, { ...line, has: mo.unit && e.target.value !== '' ? Number(e.target.value) : e.target.value })} />
              {mo.unit && <input type="text" className="gb-path-unit" aria-label="Unit" placeholder="unit" value={line.unit || ''}
                onChange={(e) => setEnd(i, { ...line, unit: e.target.value })} />}
            </>
          )}
          {line.using?.length > 0 && value.startsWith('status') && (
            <span className="gb-small gb-muted">using {line.using.map((t) => menu.names?.[t] || t).join(' and ')}</span>
          )}
          <button type="button" className="gb-link" aria-label="Delete this line" onClick={() => removeEnd(i)}>✕</button>
        </div>
      </li>
    );
  };

  const approve = async () => {
    const unsaid = path.end.find(blank);
    if (unsaid) {
      setError(`Write what ${path.objects.find((o) => o.id === unsaid.object)?.name || unsaid.object} must say, or delete that line.`);
      return;
    }
    if (await flush()) onApprove();
  };

  return (
    <div className="gb-flow gb-path">
      {!editing && plain && (
        <section className="gb-panel gb-path-plain" aria-label="The job in plain words" data-tour="pe-plain">
          {plain.finds.length > 0 && (
            <>
              <h4>What the player finds</h4>
              <ul>{plain.finds.map((f) => <li key={f}>{f}</li>)}</ul>
            </>
          )}
          <h4>What the player must do</h4>
          {plain.do.length > 0
            ? <ol>{plain.do.map((d) => <li key={d}>{d}</li>)}</ol>
            : <p className="gb-small gb-muted">Nothing yet - show every detail to add what must be done.</p>}
          <p className="gb-small gb-muted">In any order. Mistakes aren&rsquo;t stopped - the player sees how it went at the end.</p>
        </section>
      )}
      {editing && (
      <>
      <p className="gb-small gb-muted">What&rsquo;s in the room and how each thing must end up once the job is done right. The player
        works it out in any order.</p>

      <section className="gb-panel" data-tour="pe-room">
        <h4>In the room</h4>
        <ul className="gb-path-list">
          {path.objects.map((o, n) => {
            const mo = m(o.id) || {};
            const status = startLine(o.id, 'status');
            const place = startLine(o.id, 'place');
            const says = startLine(o.id, 'says');
            const statusOpts = (mo.start || []).filter((x) => x.value.startsWith('status'));
            const placeOpts = (mo.start || []).filter((x) => x.value.startsWith('rel'));
            return (
              <li key={`obj-${n}`} className="gb-path-row">
                <div className="gb-path-line">
                  <input type="text" aria-label="Object name" value={o.name} maxLength={60} onChange={(e) => renameObject(n, e.target.value)} />
                  <select aria-label="Object type" value={o.carry ? NEW : o.type} onChange={(e) => setKind(n, e.target.value)}>
                    <option value={NEW}>New - describe it</option>
                    {!o.carry && !menu.types.some((t) => t.type === o.type) && <option value={o.type}>{o.type}</option>}
                    {menu.types.map((t) => <option key={t.type} value={t.type} title={t.for}>{t.name}</option>)}
                  </select>
                  {o.carry && (
                    <>
                      <select aria-label={`How ${o.name} moves`} value={o.carry} onChange={(e) => setObject(n, { carry: e.target.value })}>
                        {CARRY.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                      <select aria-label={`What ${o.name} holds`} value={o.holds || ''}
                        onChange={(e) => setObject(n, { holds: e.target.value || undefined, inside: undefined })}>
                        {HOLDS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                      <span className="gb-path-obj">
                        <input type="text" className="gb-path-word" aria-label={`How ${o.name} starts`} placeholder="starts (optional)"
                          value={o.states?.[0] || ''} onChange={(e) => setWord(n, 0, e.target.value)} />
                        →
                        <input type="text" className="gb-path-word" aria-label={`How ${o.name} must end`} placeholder="ends"
                          value={o.states?.[1] || ''} onChange={(e) => setWord(n, 1, e.target.value)} />
                      </span>
                      {o.holds === 'in' && o.states?.[0] && o.states?.[1] && (
                        <select aria-label={`When what's in ${o.name} can be seen`} value={o.inside || ''}
                          onChange={(e) => setObject(n, { inside: e.target.value || undefined })}>
                          <option value="">contents always seen</option>
                          {o.states.map((w) => <option key={w} value={w}>contents seen when {w}</option>)}
                        </select>
                      )}
                    </>
                  )}
                  {!o.carry && mo.named && o.states && (
                    <span className="gb-path-obj">
                      <input type="text" className="gb-path-word" aria-label={`How ${o.name} starts`} placeholder="starts" value={o.states[0] || ''}
                        onChange={(e) => setObject(n, { states: [e.target.value, o.states[1] || ''] })} />
                      →
                      <input type="text" className="gb-path-word" aria-label={`How ${o.name} must end`} placeholder="ends" value={o.states[1] || ''}
                        onChange={(e) => setObject(n, { states: [o.states[0] || '', e.target.value] })} />
                    </span>
                  )}
                  {!o.carry && !(mo.named && o.states) && statusOpts.length > 0 && (
                    <select aria-label="Starts" value={status ? `status:${status.status}` : ''}
                      onChange={(e) => setStart(o.id, 'status', e.target.value ? lineFor(o.id, e.target.value) : null)}>
                      <option value="">starts as normal</option>
                      {statusOpts.map((x) => <option key={x.value} value={x.value}>starts {x.label}</option>)}
                    </select>
                  )}
                  {placeOpts.length > 0 && (
                    <select aria-label="Starts at" value={place ? valueOf(place) : ''}
                      onChange={(e) => setStart(o.id, 'place', e.target.value ? lineFor(o.id, e.target.value) : null)}>
                      <option value="">in the room</option>
                      {placeOpts.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </select>
                  )}
                  <button type="button" className="gb-link" aria-label={`Remove ${o.name}`} onClick={() => removeObject(n)}>✕</button>
                </div>
                {mo.new && (
                  <div className="gb-path-looks" aria-label={`How ${o.name} is drawn`}>
                    <input type="text" maxLength={220} aria-label={`How ${o.name} looks`} placeholder="how it looks, for its picture (optional)"
                      value={o.looks || ''} onChange={(e) => setObject(n, { looks: e.target.value || null })} />
                    {(mo.words || []).map((w) => (
                      <input key={w} type="text" maxLength={220} aria-label={`How ${o.name} looks when ${w}`} placeholder={`when ${w}`}
                        value={o.state_looks?.[w] || ''}
                        onChange={(e) => setObject(n, { state_looks: { ...(o.state_looks || {}), [w]: e.target.value } })} />
                    ))}
                  </div>
                )}
                {mo.kind !== 'person' && !mo.says && (
                  <input type="text" className="gb-path-says" maxLength={40} aria-label={`Words on ${o.name}`}
                    placeholder="words printed on it, shown as a tag (optional) - e.g. Water"
                    value={o.label || ''} onChange={(e) => setObject(n, { label: e.target.value || undefined })} />
                )}
                {mo.says && (
                  <input type="text" className="gb-path-says" maxLength={140} aria-label={`What ${o.name} says`} placeholder="what they say at the start (optional)"
                    value={says?.text || ''} onChange={(e) => setStart(o.id, 'says', e.target.value ? { object: o.id, event: 'talks', text: e.target.value } : null)} />
                )}
              </li>
            );
          })}
        </ul>
        <button type="button" className="gb-link" onClick={addObject}>+ Add an object</button>
      </section>

      <section className="gb-panel" data-tour="pe-end">
        <h4>How it must end</h4>
        <ul className="gb-path-list">
          {path.end.map((x, i) => (Array.isArray(x)
            ? x.map((y, j) => (
              <li key={`grp-${i}-${j}`} className="gb-path-row gb-small">{y.object}: {valueOf(y).split(':').pop()} (in order)
                {j === 0 && <button type="button" className="gb-link" aria-label="Delete these lines" onClick={() => removeEnd(i)}>✕</button>}
              </li>
            ))
            : endRow(x, i)))}
        </ul>
        <button type="button" className="gb-link" onClick={addEnd}>+ Add a line</button>
      </section>
      </>
      )}

      {notes.length > 0 && (
        <details className="gb-panel gb-panel-warn">
          <summary className="gb-small">Adjusted for you ({notes.length})</summary>
          <ul className="gb-small">{notes.map((n) => <li key={n}>{n}</li>)}</ul>
        </details>
      )}

      {error && <div className="gb-error" role="alert"><span>{error}</span></div>}
      <div className="gb-actions">
        {(!view.approved || changed) && (
          <button type="button" className="gb-btn" data-tour="pe-approve" disabled={busy || saving || path.objects.length === 0} onClick={approve}>
            {view.approved ? 'Approve changes' : 'Approve design'}
          </button>
        )}
        {plain && (
          <button type="button" className="gb-btn gb-btn-quiet" disabled={saving} data-tour="pe-details"
            onClick={async () => { if (editing) await flush(); setEditing(!editing); }}>
            {editing ? 'Show plain words' : 'Show every detail'}
          </button>
        )}
        <span className="gb-small gb-muted" role="status">{saving ? 'Saving…' : !view.approved || changed ? 'Writes the words (1 AI call), then builds it' : ''}</span>
      </div>
    </div>
  );
}
